import { pension, mra, birthYearOf, birthDateOf, scdOf, exactAge, serviceAt, dateAtAge, addMonths } from "@/lib/fers";

export { fmtDate } from "@/lib/fers";

const HOURS_PER_YEAR = 2087;
const SICK_ACCRUAL = 104;

function dateAt(months) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + months + 1);
  d.setDate(0);
  return d;
}

export function scanRetirementDates(p) {
  const g = ((Number(p.salary_growth) || 0) + (Number(p.step_increase_pct) || 0)) / 100;
  const by = birthYearOf(p), bd = birthDateOf(p), scd = scdOf(p);
  const age0 = exactAge(p);
  const maxAge = Math.max(62, Number(p.retire_age) || 62) + 5;
  const totalMonths = Math.max(1, Math.round((Math.min(70, maxAge) - age0) * 12));
  const sick0 = Number(p.sick_leave_hours) || 0;
  const hourly = (Number(p.salary) || 0) / HOURS_PER_YEAR;
  const rows = [];
  for (let m = 0; m <= totalMonths; m++) {
    const date = dateAt(m);
    const age = bd ? exactAge(p, date) : p.age + m / 12;
    const sickHours = sick0 + (SICK_ACCRUAL * m) / 12;
    const sickYears = p.retirement_system === "FERS" || p.retirement_system === "CSRS" ? sickHours / HOURS_PER_YEAR : 0;
    const service = scd ? serviceAt(p, date) : (Number(p.service_years) || 0) + m / 12;
    const h3 = Number(p.high3_override) > 0 ? Number(p.high3_override) : (Number(p.salary) || 0) * Math.pow(1 + g, (m - 18) / 12);
    const full = pension({ ...p, birth_date: "", service_start_date: "", birth_year: by, age, retire_age: age, service_years: service, high3_override: h3 });
    const monthly = service > 0 ? (full.annual / 12) * ((full.service + sickYears) / full.service) : 0;
    const supp = full.supplement / 12;
    const startAge = full.startAge;
    const life = Number(p.life_expectancy) || 90;
    const v = Math.pow(1 + (Number(p.retire_return) || 5) / 100, -1 / 12);
    const pv = (amt, fromM, months) => (months > 0 ? amt * ((1 - Math.pow(v, months)) / (1 - v)) * Math.pow(v, fromM) : 0);
    const startM = Math.round((startAge - age0) * 12);
    const lifetime = full.eligible ? pv(monthly, startM, Math.round((life - startAge) * 12)) + pv(supp, startM, Math.round((62 - startAge) * 12)) : 0;
    rows.push({ m, date, age, service, sickYears, h3, eligible: full.eligible, type: full.type, multiplier: full.multiplier, reduction: full.reduction,
      monthly, supp, startAge, lifetime, leavePayout: (Number(p.annual_leave_hours) || 0) * hourly * Math.pow(1 + g, m / 12) });
  }
  const firstWhere = (fn) => rows.find(fn);
  const milestones = [];
  const push = (label, r, why, exact = null) => r && milestones.push({ label, row: r, why, exact });
  if (p.retirement_system === "FERS") {
    push("Earliest MRA+10 (reduced)", firstWhere((r) => r.eligible && r.reduction > 0), "Reduced 5% for each year under 62; no FERS Supplement.");
    push("Earliest unreduced annuity", firstWhere((r) => r.eligible && r.reduction === 0 && r.startAge <= r.age + 0.01), "No age penalty.");
    push("First date with the FERS Supplement", firstWhere((r) => r.supp > 0), "Supplement bridges to Social Security until 62.");
    push("Age 62 with 20 years (1.1% multiplier)", firstWhere((r) => r.multiplier >= 1.1), "10% bigger annuity for life.");
    push(`Minimum retirement age ${mra(by).toFixed(1)}`, firstWhere((r) => r.age >= mra(by)), "MRA reached.", dateAtAge(p, mra(by)));
  } else {
    push("Earliest eligible pension", firstWhere((r) => r.eligible), "First month a pension is payable.");
  }
  push("30 years of service", firstWhere((r) => r.service >= 30), "Service milestone.", scd ? addMonths(scd, 360) : null);
  push("20 years of service", firstWhere((r) => r.service >= 20), "Service milestone.", scd ? addMonths(scd, 240) : null);
  const elig = rows.filter((r) => r.eligible && r.startAge <= r.age + 0.01);
  const bestLifetime = elig.reduce((b, r) => (!b || r.lifetime > b.lifetime ? r : b), null);
  const bestMonthly = elig.reduce((b, r) => (!b || r.monthly + r.supp > b.monthly + b.supp ? r : b), null);
  const unreduced = firstWhere((r) => r.eligible && r.reduction === 0 && r.startAge <= r.age + 0.01);
  const leaveYearEnd = unreduced ? rows.find((r) => r.m >= unreduced.m && r.date.getMonth() === 0) : null;
  push("End of leave year after unreduced date", leaveYearEnd, "Retiring right after the leave year resets lets you use or be paid for the full annual leave balance.");
  milestones.sort((a, b) => a.row.m - b.row.m);
  return { rows, milestones, bestLifetime, bestMonthly, hourly, precise: { birth: !!bd, scd: !!scd } };
}
